// components/ProfileDetails.tsx (simplified usage)
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FaHeart, FaBookmark, FaRegBookmark, FaInfoCircle, FaHeartbeat,
  FaTransgender, FaTint, FaStar, FaUserCheck, FaRing, FaPray,
  FaUserGraduate, FaGlobe, FaMapMarkerAlt, FaBed, FaSmoking,
  FaCoffee, FaGraduationCap, FaBriefcase, FaMosque, FaBirthdayCake,
  FaRuler, FaUsers, FaChild, FaVenusMars,
  FaEye,
  FaCheckCircle,
  FaFlag
} from 'react-icons/fa';
import { MdHealthAndSafety, MdLocationOn } from 'react-icons/md';
import { GiHealthNormal, GiMuscleUp, GiHairStrands } from 'react-icons/gi';
import { Crown } from 'lucide-react';
import { useFetchQuery } from '../hooks/useQuery';
import { MY_ENDPOINTS } from "@raashidh/shared";
import myIcons from '@/assets/Icons';
import MyTabbedDetails from '@/MY_COMPONENTS/MY_TABBED_DETAILS';
import { TabConfig, SectionConfig, ContentConfig, ActionButtonConfig } from '@/MY_COMPONENTS/MY_TABBED_DETAILS/types';

// Configuration
const TABS: TabConfig[] = [
  { id: 'about', label: 'About', icon: FaInfoCircle, color: 'blue' },
  { id: 'lookingFor', label: 'Looking For', icon: FaHeart, color: 'pink' },
];

const SECTIONS: SectionConfig[] = [
  { id: 'basic_info', label: 'Basic Information', icon: FaInfoCircle, color: 'blue', type: 'headercard' },
  { id: 'marital', label: 'Marital Status', icon: FaHeart, color: 'emerald', type: 'headercard' },
  { id: 'religious', label: 'Religious Profile', icon: FaMosque, color: 'green', type: 'headercard' },
  { id: 'interests', label: 'Interests', icon: FaHeartbeat, color: 'pink', type: 'headercard' },
  { id: 'location', label: 'Location', icon: FaMapMarkerAlt, color: 'purple', type: 'headercard' }, // ADD THIS LINE
  { id: 'looking_for_preferences', label: 'Partner Preferences', icon: FaHeart, color: 'red', type: 'headercard' },
  { id: 'location_preferences', label: 'Location Preferences', icon: FaMapMarkerAlt, color: 'purple', type: 'headercard' },
  { id: 'lifestyle_preferences', label: 'Lifestyle', icon: MdHealthAndSafety, color: 'teal', type: 'headercard' },
  { id: 'education_career', label: 'Education & Career', icon: FaGraduationCap, color: 'indigo', type: 'headercard' },
];

const CONTENTS: ContentConfig[] = [
  // Basic Info
  { tab: 'about', section: 'basic_info', type: 'card', key: 'age', label: 'Age', icon: FaBirthdayCake, format: (val: number) => `${val} years` },
  { tab: 'about', section: 'basic_info', type: 'card', key: 'gender', label: 'Gender', icon: FaTransgender, format: (val: string) => val === 'm' ? 'Male' : 'Female' },
  { tab: 'about', section: 'basic_info', type: 'card', key: 'tribe', label: 'Tribe', icon: FaUsers },
  { tab: 'about', section: 'basic_info', type: 'card', key: 'ethnicity', label: 'Ethnicity', icon: FaGlobe },
  { tab: 'about', section: 'basic_info', type: 'card', key: 'height_cm', label: 'Height', icon: FaRuler, format: (val: number) => `${val} cm` },
  { tab: 'about', section: 'basic_info', type: 'card', key: 'body_build', label: 'Body Build', icon: GiMuscleUp },
  { tab: 'about', section: 'basic_info', type: 'card', key: 'complexion', label: 'Complexion', icon: FaTint },
  { tab: 'about', section: 'basic_info', type: 'card', key: 'hair_color', label: 'Hair Color', icon: GiHairStrands },
  { tab: 'about', section: 'basic_info', type: 'card', key: 'eye_color', label: 'Eye Color', icon: FaEye },
  
  // Marital Status
  { tab: 'about', section: 'marital', type: 'card', key: 'marital_status', label: 'Status', icon: FaRing },
  { tab: 'about', section: 'marital', type: 'card', key: 'has_children', label: 'Children', icon: FaChild },
  { tab: 'about', section: 'marital', type: 'card', key: 'kids_count', label: 'Number of Children', icon: FaChild },
  { tab: 'about', section: 'marital', type: 'card', key: 'kids_details', label: 'Children Details', icon: FaChild },
  { tab: 'about', section: 'marital', type: 'card', key: 'readiness', label: 'Readiness', icon: FaStar },
  
  // Religious
  { tab: 'about', section: 'religious', type: 'card', key: 'religion', label: 'Religion', icon: FaMosque },
  { tab: 'about', section: 'religious', type: 'card', key: 'sect', label: 'Sect', icon: FaPray },
  { tab: 'about', section: 'religious', type: 'card', key: 'practice_level', label: 'Practice Level', icon: FaStar },
  { tab: 'about', section: 'religious', type: 'card', key: 'islamic_education', label: 'Islamic Education', icon: FaUserGraduate },
  { tab: 'about', section: 'religious', type: 'card', key: 'hajj_status', label: 'Hajj Status', icon: FaCheckCircle },
  
  // Interests
  { tab: 'about', section: 'interests', type: 'tags', key: 'interests', label: 'Interests', icon: FaHeartbeat },
  
  // Education & Career
  { tab: 'about', section: 'education_career', type: 'card', key: 'education_level', label: 'Education', icon: FaGraduationCap },
  { tab: 'about', section: 'education_career', type: 'card', key: 'occupation', label: 'Occupation', icon: FaBriefcase },
  
  // Location
  { tab: 'about', section: 'location', type: 'card', key: 'city', label: 'City', icon: FaMapMarkerAlt },
  { tab: 'about', section: 'location', type: 'card', key: 'country', label: 'Country', icon: FaGlobe },
  { tab: 'about', section: 'location', type: 'card', key: 'origin_state', label: 'State', icon: FaFlag },
  
 // Location - Map view (THIS IS THE MAP)
  { 
    tab: 'about', 
    section: 'location',  // Same section as above - will appear in same card
    type: 'map', 
    key: 'location_map',  // Unique key
    label: 'Location Map', 
    latKey: 'public_location_lat', 
    lngKey: 'public_location_lon' 
  },

  // Looking For Preferences
  { tab: 'lookingFor', section: 'looking_for_preferences', type: 'card', key: 'gender', label: 'Seeking', icon: FaVenusMars, lookingFor: true, format: (val: string) => val === 'f' ? 'Female' : 'Male' },
  { tab: 'lookingFor', section: 'looking_for_preferences', type: 'range', key: 'age', label: 'Age Range', icon: FaBirthdayCake, lookingFor: true, format: (val: any, item: any) => `${item.age_min} - ${item.age_max} years` },
  { tab: 'lookingFor', section: 'looking_for_preferences', type: 'card', key: 'relocation', label: 'Willing to Relocate', icon: FaGlobe, lookingFor: true, format: (val: string) => val === 'Yes' ? 'Yes ✓' : 'No ✗' },
  { tab: 'lookingFor', section: 'looking_for_preferences', type: 'card', key: 'polygamy', label: 'Accepts Polygamy', icon: FaUsers, lookingFor: true, format: (val: string) => val === 'Yes' ? 'Yes ✓' : 'No ✗' },
  { tab: 'lookingFor', section: 'looking_for_preferences', type: 'card', key: 'accepts_children', label: 'Accepts Children', icon: FaChild, lookingFor: true },
  
  // Location Preferences
  { tab: 'lookingFor', section: 'location_preferences', type: 'card', key: 'countries', label: 'Country', icon: FaGlobe, lookingFor: true },
  { tab: 'lookingFor', section: 'location_preferences', type: 'card', key: 'cities', label: 'City', icon: FaMapMarkerAlt, lookingFor: true },
  { tab: 'lookingFor', section: 'location_preferences', type: 'card', key: 'living_in', label: 'Prefers Living In', icon: FaBed, lookingFor: true },
  
  // Lifestyle Preferences
  { tab: 'lookingFor', section: 'lifestyle_preferences', type: 'card', key: 'smoking', label: 'Smoking', icon: FaSmoking, lookingFor: true },
  { tab: 'lookingFor', section: 'lifestyle_preferences', type: 'card', key: 'alcohol', label: 'Alcohol', icon: FaCoffee, lookingFor: true },
  { tab: 'lookingFor', section: 'lifestyle_preferences', type: 'card', key: 'health_status', label: 'Health', icon: GiHealthNormal, lookingFor: true },
];

const ACTION_BUTTONS: ActionButtonConfig[] = [
  { id: 'like', labelKey: 'Like', icon: FaHeart, activeIcon: FaHeart, color: 'red', bgColor: 'bg-red-500' },
  { id: 'super_like', labelKey: 'Super Like', icon: Crown, activeIcon: Crown, color: 'green', bgColor: 'bg-green-500' },
  { id: 'save', labelKey: 'Save', icon: FaRegBookmark, activeIcon: FaBookmark, color: 'yellow', bgColor: 'bg-yellow-500' }
];

export default function ProfileDetails() {
  const { userId } = useParams();
  const navigate = useNavigate();
  
  const { data, isLoading, error, refetch } = useFetchQuery(
    ['profile', userId],
    MY_ENDPOINTS.PROFILES_ENDPOINTS.GET_PROFILE.path.replace(':userId', userId || ''),
    { enabled: !!userId }
  );
  
  const profile = data?.data;
  
  const handleAction = (actionId: string, isAdding: boolean) => {
    // Handle like, save, super_like actions
    console.log(`${actionId}: ${isAdding ? 'added' : 'removed'}`);
  };
  
  const handleSendRequest = () => {
    // Handle send request
    console.log('Send request');
  };
  
  return (
    <MyTabbedDetails
      data={profile}
      isLoading={isLoading}
      error={error}
      tabs={TABS}
      sections={SECTIONS}
      contents={CONTENTS}
      actionButtons={ACTION_BUTTONS}
      title={`${profile?.nickname} (${profile?.marital_status})`}
      headColor={profile?.gender === 'm' ? 'bg-gradient-to-r from-blue-500 to-blue-700' : 'bg-gradient-to-r from-pink-500 to-pink-700'}
      subtitle={`${profile?.age} years old`}
      location={[profile?.city, profile?.country].filter(Boolean).join(', ')}
      joinDate={profile?.created_at}
      profileViews={profile?.profile_views}
      completionPercentage={profile?.completion_percentage}
      status={profile?.seeking_status}
      isActive={profile?.is_active}
      compatibilityScore={profile?.compatibility_score}
      summary={profile?.looking_for_summary}
      //profileImage={profile?.photos?.[0] || (profile?.gender === 'm' ? myIcons.man : myIcons.woman)}
      profileImage={(profile?.gender === 'm' ? myIcons.man : myIcons.woman)}
      onBack={() => navigate(-1)}
      onAction={handleAction}
      onSendRequest={handleSendRequest}
      getValue={(key, fromLookingFor) => {
        if (fromLookingFor) return profile?.looking_for?.[key];
        return profile?.[key];
      }}
    />
  );
}